import { useEffect, useState } from "react";
import { Inbox, Phone, Mail, Trash2, LifeBuoy, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const STATUSES = ["new", "in progress", "done"];

export default function Dashboard() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/requests").then((r) => setRequests(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.put(`/requests/${id}/status?status=${encodeURIComponent(status)}`);
    load();
  };
  const remove = async (id) => {
    await api.delete(`/requests/${id}`);
    toast.success("Deleted");
    load();
  };

  const filtered = filter === "all" ? requests : requests.filter((r) => r.status === filter);
  const counts = { new: requests.filter((r) => r.status === "new").length, total: requests.length };

  return (
    <div>
      <span className="overline text-brand-terracotta">Inbox</span>
      <h1 className="font-display text-4xl tracking-tight mt-1 mb-6">Guest Requests</h1>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
        {[["New", counts.new], ["Total", counts.total]].map(([label, val]) => (
          <div key={label} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm" data-testid={`stat-${label.toLowerCase()}`}>
            <div className="overline text-brand-forest/50">{label}</div>
            <div className="font-display text-3xl mt-1">{val}</div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.replace(/ /g, "-")}`}
            className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition-colors ${filter === s ? "bg-brand-forest text-white" : "bg-white border border-black/5 text-brand-forest/70 hover:text-brand-terracotta"}`}>
            {s}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-brand-forest/40">
          <Inbox size={48} className="mx-auto mb-3" />
          <p>No requests here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`request-${r.id}`} className="bg-white rounded-2xl p-5 border border-black/5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${r.type === "service" ? "bg-brand-terracotta/15 text-brand-terracotta" : "bg-brand-forest/10 text-brand-forest"}`}>
                    {r.type === "service" ? <Sparkles size={18} /> : <LifeBuoy size={18} />}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{r.guest_name}</span>
                      {r.addon_title && <span className="text-xs bg-brand-sand text-brand-terracotta rounded-full px-2 py-0.5 font-medium">{r.addon_title}</span>}
                    </div>
                    <div className="text-xs text-brand-forest/50 mt-0.5">{new Date(r.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={() => remove(r.id)} data-testid={`request-delete-${r.id}`} className="text-brand-forest/30 hover:text-destructive transition-colors">
                  <Trash2 size={18} />
                </button>
              </div>
              {r.message && <p className="text-brand-forest/80 mt-3 leading-relaxed">{r.message}</p>}
              {r.contact && (
                <div className="mt-3 flex items-center gap-1.5 text-sm text-brand-terracotta">
                  {r.contact.includes("@") ? <Mail size={14} /> : <Phone size={14} />} {r.contact}
                </div>
              )}
              <div className="mt-4 flex gap-2">
                {STATUSES.map((s) => (
                  <Button key={s} variant={r.status === s ? "default" : "outline"} size="sm"
                    onClick={() => setStatus(r.id, s)} data-testid={`request-status-${r.id}-${s.replace(/ /g, "-")}`}
                    className={`rounded-full capitalize text-xs ${r.status === s ? "bg-brand-forest text-white" : ""}`}>
                    {s}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
